import ActionTextIcon from "@/components/icons/ActionTextIcon";
import Ellipsis from "@/components/views/Ellipsis";
import { formatLocaleNettoPoints, sanitize } from "@/utils/formatUtils";
import { isDefined } from "@/utils/utils";
import { Dropdown } from "antd";
import classNames from "classnames";
import React from "react";
import { useTranslation } from "react-i18next";
import { generatePath, Link } from "react-router";
import { ACCESS_TREE_PATH_DELIMITER } from "../../../../common/constants";
import { Permission } from "../../../../common/security/enums";
import { useCurrentAgentAccount, useHasPermissions } from "../../../auth/hooks";
import { AGENT_ROUTE_PATHS } from "../../paths";
import { AgentTreeNode } from "../../types";
import { AgentTreeColumnsWidth, formatAgentIdNumber, isAgentTipper } from "../../utils";
import { AgentTipperTag } from "../tags/AgentTipperTag";
import { AgentTypeTag } from "../tags/AgentTypeTag";
import styles from "./AgentTreeNodeTitle.module.scss";

const highlightSearchKeyword = (str?: string, sanitizedSearchKeyword?: string): React.ReactNode => {
  if (sanitizedSearchKeyword && str) {
    const keywordStartIndex = sanitize(str)?.indexOf(sanitizedSearchKeyword);

    if (isDefined(keywordStartIndex) && keywordStartIndex > -1) {
      const beforeKeyword = str.substring(0, keywordStartIndex);
      const keyword = str.substring(keywordStartIndex, keywordStartIndex + sanitizedSearchKeyword.length);
      const afterKeyword = str.substring(keywordStartIndex + sanitizedSearchKeyword.length);
      return (
        <span>
          {beforeKeyword}
          <span className={styles.searchKeyword}>{keyword}</span>
          {afterKeyword}
        </span>
      );
    }
  }

  return <span>{str}</span>;
};

interface Props {
  agent: AgentTreeNode;
  sanitizedSearchKeyword?: string;
  rootAgentAccessTreePath?: string;
}

const getAgentAccessTreePathDepth = (treePath?: string): number => {
  return treePath?.split(ACCESS_TREE_PATH_DELIMITER).length ?? 0;
};

const TREE_INDENT_SIZE = 24;

export const AgentTreeNodeTitle = ({ agent, sanitizedSearchKeyword, rootAgentAccessTreePath }: Props) => {
  const { t } = useTranslation();

  const hasAgentCommissionsPermission = useHasPermissions(Permission.AGENT_COMMISSIONS);
  const hasProfileCommissionsPermission = useHasPermissions(Permission.PROFILE_COMMISSIONS);
  const userAccount = useCurrentAgentAccount();

  const agentRepresentatives = agent.representatives?.map((r) => r.displayName).join(", ");

  const currentIndentSize =
    Math.max(
      0,
      getAgentAccessTreePathDepth(agent.accessTreePath) - getAgentAccessTreePathDepth(rootAgentAccessTreePath),
    ) * TREE_INDENT_SIZE;

  const pointsWidth = hasAgentCommissionsPermission ? 0 : AgentTreeColumnsWidth.POINTS;

  return (
    <span className={classNames(styles.agentTreeNode, agent.deactivated ? styles.deactivated : "")}>
      <span
        style={{
          display: "inline-flex",
          width: AgentTreeColumnsWidth.AGENT - currentIndentSize - 44 + pointsWidth,
          paddingRight: 8,
        }}
      >
        <AgentTypeTag type={agent.type} />

        <span style={{ display: "inline-block", paddingRight: 8, width: `calc(100% - 40px)` }}>
          <Ellipsis tooltip={agent.aggregatedName + (agentRepresentatives ? " | " + agentRepresentatives : "")}>
            <Link className={styles.noLinkColor} to={generatePath(AGENT_ROUTE_PATHS.detail.to, { id: agent.id })}>
              {highlightSearchKeyword(agent.aggregatedName, sanitizedSearchKeyword)}
            </Link>

            {agentRepresentatives ? (
              <i className={styles.selectOptionSecondaryRow}>
                {" "}
                | {highlightSearchKeyword(agentRepresentatives, sanitizedSearchKeyword)}
              </i>
            ) : undefined}
          </Ellipsis>
        </span>
      </span>

      <span className={styles.agentTreeNodeColumn} style={{ width: AgentTreeColumnsWidth.ID_NUMBER }}>
        <Ellipsis>{highlightSearchKeyword(formatAgentIdNumber(agent), sanitizedSearchKeyword)}</Ellipsis>
      </span>

      <span className={styles.agentTreeNodeColumn} style={{ width: AgentTreeColumnsWidth.IDENTIFIER }}>
        <Ellipsis>{highlightSearchKeyword(agent.identifier, sanitizedSearchKeyword)}</Ellipsis>
      </span>

      <span className={styles.agentTreeNodeColumn} style={{ width: AgentTreeColumnsWidth.LEVEL }}>
        <Ellipsis>{highlightSearchKeyword(agent.commissionsSettings.level?.code, sanitizedSearchKeyword)}</Ellipsis>
      </span>

      <span className={styles.agentTreeNodeColumn} style={{ width: AgentTreeColumnsWidth.RANK }}>
        <Ellipsis>{highlightSearchKeyword(agent.commissionsSettings.rank?.name, sanitizedSearchKeyword)}</Ellipsis>
      </span>

      <span
        className={styles.agentTreeNodeColumn}
        style={{
          width: AgentTreeColumnsWidth.POINTS,
          justifyContent: "end",
          display: pointsWidth > 0 ? "none" : "inline-flex",
        }}
      >
        <Ellipsis>
          {highlightSearchKeyword(
            isDefined(agent.commissionsSettings.careerPointAmount)
              ? formatLocaleNettoPoints(agent.commissionsSettings.careerPointAmount, 0)
              : undefined,
            sanitizedSearchKeyword,
          )}
        </Ellipsis>
      </span>

      <span className={styles.agentTreeNodeColumn} style={{ width: AgentTreeColumnsWidth.TIPPER }}>
        {isAgentTipper(agent) ? <AgentTipperTag style={{ margin: 0 }} /> : undefined}
      </span>

      <span
        className={classNames(styles.agentTreeNodeColumn)}
        style={{ width: AgentTreeColumnsWidth.ACTIONS, padding: "0 0 0 8px", justifyContent: "end" }}
      >
        <Dropdown
          menu={{
            items: [
              {
                key: "show",
                label: (
                  <ActionTextIcon
                    color="blue"
                    icon="eye"
                    text={t("common.show")}
                    path={generatePath(AGENT_ROUTE_PATHS.detail.to, { id: agent.id })}
                  />
                ),
              },
              hasAgentCommissionsPermission && (hasProfileCommissionsPermission || agent.id !== userAccount?.agent.id)
                ? {
                    key: "commissions",
                    label: (
                      <ActionTextIcon
                        color="green"
                        icon="euro"
                        text={t("common.commissions")}
                        path={generatePath(AGENT_ROUTE_PATHS.commissions.to, { id: agent.id })}
                      />
                    ),
                  }
                : null,
            ],
          }}
          trigger={["click"]}
        >
          <span>
            <ActionTextIcon icon="menu-unfold" color="blue" text={t("common.options")} />
          </span>
        </Dropdown>
      </span>
    </span>
  );
};
